import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { PackageCheck, Check } from 'lucide-react';
import { useAmendMode, AmendBanner } from './useAmendMode';
import './NativeInventoryAdjustmentNew.css';

// Receive PO (Native) — book goods in off a purchase order without NetSuite.
// This is the screen the dock uses during a NetSuite lockout: open POs and
// their remaining lines come from the purchase_orders mirror, never live NS.
// Posting creates a cost layer per line at PO cost, adds native on-hand at the
// receiving location, posts DR inventory / CR 2035 received-not-billed, and
// advances the PO's received quantities and lifecycle.
// Reuses the adjustment screen's stylesheet (same house template).

const money = (n) => {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
};
const qty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
};
const newRef = () => `REC-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

export default function NativeReceiptNew() {
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(null);
  const [writesEnabled, setWritesEnabled] = useState(null);

  const [openPos, setOpenPos] = useState([]);
  const [poQuery, setPoQuery] = useState('');
  const [po, setPo] = useState(null);
  const [loadingPo, setLoadingPo] = useState(false);

  // lines: [{ lineNumber, sku, name, ordered, received, open, unitCost,
  //           isWeightItem, resolved, receiveQty, binCode }]
  const [lines, setLines] = useState([]);

  const [packingSlip, setPackingSlip] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const sourceKey = useRef(newRef());
  const amend = useAmendMode();
  const [amendOfNumber, setAmendOfNumber] = useState(null);

  useEffect(() => {
    axios.get('/api/native-inventory-adjustments/locations')
      .then(({ data }) => setLocations((data.locations || []).filter((l) => l.isActive !== false)))
      .catch(() => setLocations([]));
    axios.get('/api/native-receipts')
      .then(({ data }) => setWritesEnabled(data.writesEnabled === true))
      .catch(() => setWritesEnabled(null));
  }, []);

  // Amend (?amendOf=<receipt id>): load the receipt's PO, then overwrite each
  // line's "receive now" with what the ORIGINAL receipt booked, so the operator
  // corrects the numbers that were actually posted.
  //
  // The "open" quantities beside them are the PO as it stands RIGHT NOW, which
  // still counts this receipt — the reversal has not happened yet. So the
  // over-receive warning on this screen reads pessimistically during an amend.
  // The real check runs server-side after the reversal, against the PO the
  // amendment is actually posting into.
  useEffect(() => {
    if (!amend.amendOf || locations.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-receipts/${encodeURIComponent(amend.amendOf)}`);
        if (cancelled || !data.receipt) return;
        const r = data.receipt;
        setAmendOfNumber(r.receipt_number);
        setPackingSlip(r.packing_slip_number || '');
        setNotes(r.notes || '');
        const loc = locations.find((l) => l.id === r.location_id);
        if (loc) setLocation(loc);
        if (r.native_po_id == null) return;
        await loadPo(String(r.native_po_id));
        if (cancelled) return;
        const byLine = new Map((data.lines || []).map((l) => [
          Number(l.po_line_number),
          { qty: Number(l.po_quantity_received ?? l.quantity_received), bin: l.bin_location || '' },
        ]));
        setLines((ls) => ls.map((l) => (byLine.has(Number(l.lineNumber))
          ? { ...l, receiveQty: String(byLine.get(Number(l.lineNumber)).qty), binCode: byLine.get(Number(l.lineNumber)).bin }
          : { ...l, receiveQty: '0' })));
      } catch { /* prefill is best-effort; the form stays blank */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const searchPos = async (q) => {
    setPoQuery(q);
    if (q.trim().length < 2) { setOpenPos([]); return; }
    try {
      const { data } = await axios.get('/api/native-receipts/open-pos', { params: { q: q.trim() } });
      setOpenPos(data.purchaseOrders || []);
    } catch {
      setOpenPos([]);
    }
  };

  const loadPo = async (ref) => {
    setLoadingPo(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/native-receipts/po-lines', { params: { po: ref } });
      const open = (data.lines || []).map((l) => ({
        ...l,
        receiveQty: String(l.open),
        binCode: '',
      }));
      if (open.length === 0) {
        setError(`${data.purchaseOrder.poNumber} has no open lines to receive.`);
        return;
      }
      setPo(data.purchaseOrder);
      setLines(open);
      setOpenPos([]);
      // Default the receiving location to the PO's ship-to when it has one.
      if (data.purchaseOrder.shipToLocationId) {
        const match = locations.find((l) => l.id === data.purchaseOrder.shipToLocationId);
        if (match) setLocation(match);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingPo(false);
    }
  };

  const setLineField = (lineNumber, field, v) =>
    setLines((ls) => ls.map((l) => (l.lineNumber === lineNumber ? { ...l, [field]: v } : l)));
  const removeLine = (lineNumber) => setLines((ls) => ls.filter((l) => l.lineNumber !== lineNumber));
  // Dropping the PO starts a NEW receipt, so it gets a new idempotency key.
  // Everything else (including a failed post) keeps the current key.
  const clearPo = () => { setPo(null); setLines([]); setError(null); sourceKey.current = newRef(); };

  const receiving = lines.filter((l) => Number(l.receiveQty) > 0);
  const totalValue = receiving.reduce((s, l) => s + Number(l.receiveQty) * Number(l.unitCost || 0), 0);
  const unresolved = receiving.filter((l) => !l.resolved);
  const weightLines = receiving.filter((l) => l.isWeightItem);
  const overReceipt = receiving.filter((l) => Number(l.receiveQty) > l.open);
  const canPost = po && location && receiving.length > 0 && unresolved.length === 0 && !submitting
    && (!amend.isAmend || amend.amendReasonValid);

  const post = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(amend.postUrl('/api/native-receipts'), {
        sourceKey: sourceKey.current,
        poRef: { poId: String(po.id) },
        locationId: location.id,
        packingSlipNumber: packingSlip.trim() || undefined,
        notes: notes.trim() || undefined,
        lines: receiving.map((l) => ({
          lineNumber: l.lineNumber,
          quantity: Number(l.receiveQty),
          binCode: l.binCode.trim() || undefined,
        })),
        ...amend.bodyExtras(),
      });
      setConfirmation(data);
      // Posted: this key is spent. The next receipt gets a fresh one.
      sourceKey.current = newRef();
    } catch (err) {
      // Deliberately KEEP the same sourceKey. A failed post is exactly when
      // legs may have posted server-side (the request can fail after the
      // receipt row commits, or fail in transit), so retrying with the same
      // key is what lets the server's idempotency guard return the existing
      // receipt instead of receiving the PO a second time.
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const r = confirmation.receipt;
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>{r.receipt_number} received</h2>
          {(confirmation.failedLegs || []).length > 0 && (
            <div className="nia-hint nia-tag-warn" style={{ marginBottom: 8 }}>
              Needs attention — {confirmation.failedLegs.join(' and ')} did not post. The goods are booked only on
              the legs that succeeded; a failed PO linkage also leaves the line open to a second receipt.
            </div>
          )}
          <div className="nia-confirm-grid">
            <span>Purchase order</span><strong>{r.po_number}</strong>
            <span>Received into</span><strong>{location?.name}</strong>
            <span>Lines</span><strong>{(confirmation.lines || []).length}</strong>
            <span>Value</span><strong>{money(r.total_value)}</strong>
            <span>Quantity leg</span><strong>{r.movement_status}{r.movement_error ? ` (${r.movement_error})` : ''}</strong>
            <span>Costing</span><strong>{r.costing_status}{r.costing_error ? ` (${r.costing_error})` : ''}</strong>
            <span>GL</span><strong>{r.gl_status}{r.gl_error ? ` (${r.gl_error})` : ''}</strong>
            <span>PO status</span><strong>{r.po_linkage_status}{r.po_lifecycle ? ` → ${r.po_lifecycle}` : ''}{r.po_linkage_error ? ` (${r.po_linkage_error})` : ''}</strong>
            <span>NetSuite</span><strong>{r.ns_status}</strong>
          </div>
          <p className="nia-note">{confirmation.note}</p>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={() => window.location.reload()}>Receive another PO</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><PackageCheck size={20} /> {amend.isAmend ? 'Amend Goods Receipt' : 'Receive PO (Native)'}</h1>
        {writesEnabled === false && (
          <div className="nia-banner">Posting is disabled (NATIVE_PO_RECEIVING_ENABLED is off on the Run service). Browsing works; posting will 403.</div>
        )}
      </div>

      <AmendBanner amend={amend} docLabel={amendOfNumber} what="the receipt" />

      <div className="nia-card">
        <h3>1. Purchase order</h3>
        {!po ? (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="nia-input nia-input-search"
                placeholder="PO number or vendor (e.g. PO464349)…"
                value={poQuery}
                onChange={(e) => searchPos(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && poQuery.trim()) loadPo(poQuery.trim()); }}
              />
              <button className="nia-btn" onClick={() => loadPo(poQuery.trim())} disabled={loadingPo || !poQuery.trim()}>
                {loadingPo ? 'Loading…' : 'Load open lines'}
              </button>
            </div>
            {openPos.length > 0 && (
              <div className="nia-results" style={{ marginTop: 8 }}>
                {openPos.map((p) => (
                  <button key={p.id} className="nia-result" onClick={() => loadPo(String(p.id))}>
                    <code>{p.poNumber}</code> {p.vendorName || ''}
                    <span className="nia-result-muted">{p.openLines} open line{p.openLines === 1 ? '' : 's'}, {qty(p.openUnits)} units</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="nia-chip">
            <code>{po.poNumber}</code> {po.vendorName || ''}
            {po.nsInternalId && <span className="nia-result-muted"> NS PO {po.nsInternalId}</span>}
            <button className="nia-icon-btn" onClick={clearPo}>×</button>
          </div>
        )}
      </div>

      <div className="nia-card">
        <h3>2. Receive into</h3>
        <div className="nia-locations">
          {locations.map((l) => (
            <button
              key={l.id}
              className={`nia-location ${location?.id === l.id ? 'selected' : ''}`}
              onClick={() => setLocation(l)}
            >
              {l.name}
            </button>
          ))}
        </div>
        {po && location && po.subsidiaryId != null && Number(po.subsidiaryId) !== Number(location.subsidiaryId) && (
          <div className="nia-hint nia-tag-warn" style={{ marginTop: 8 }}>
            {po.poNumber} belongs to subsidiary {po.subsidiaryId} but {location.name} is subsidiary {location.subsidiaryId} — posting will be refused.
          </div>
        )}
      </div>

      {error && <div className="nia-error">{error}</div>}

      {po && lines.length > 0 && (
        <div className="nia-card">
          <h3>3. Lines — {po.poNumber}</h3>
          <table className="nia-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="r">Ordered</th>
                <th className="r">Already in</th>
                <th className="r">Open</th>
                <th className="r">Unit cost</th>
                <th className="r">Receive now</th>
                <th>Bin</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.lineNumber}>
                  <td>
                    <code>{l.sku || l.nsItemId}</code> {l.name}
                    {!l.resolved && <span className="nia-tag nia-tag-warn">no item record</span>}
                    {l.isWeightItem && (
                      <div className="nia-hint" style={{ marginTop: 4 }}>
                        Quantities are in OUNCES (a 25 lb drum is 400). Enter what the PO states — do not
                        convert to pounds.
                      </div>
                    )}
                  </td>
                  <td className="r">{qty(l.ordered)}</td>
                  <td className="r">{qty(l.received)}</td>
                  <td className="r">{qty(l.open)}</td>
                  <td className="r">{l.unitCost == null ? '—' : money(l.unitCost)}</td>
                  <td className="r">
                    <input
                      className="nia-input nia-input-num"
                      inputMode="decimal"
                      value={l.receiveQty}
                      onChange={(e) => setLineField(l.lineNumber, 'receiveQty', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="nia-input nia-short"
                      placeholder="UNBINNED"
                      value={l.binCode}
                      onChange={(e) => setLineField(l.lineNumber, 'binCode', e.target.value)}
                    />
                  </td>
                  <td className="r">
                    <button className="nia-icon-btn" onClick={() => removeLine(l.lineNumber)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Packing slip (optional)
              <input className="nia-input" value={packingSlip} onChange={(e) => setPackingSlip(e.target.value)} maxLength={100} />
            </label>
            <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
              Notes (optional)
              <input className="nia-input" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
            </label>
          </div>

          {unresolved.length > 0 && (
            <div className="nia-hint nia-tag-warn" style={{ marginTop: 8 }}>
              No item record for {unresolved.map((l) => l.sku || l.nsItemId).join(', ')} — remove those lines or have the item synced first.
            </div>
          )}
          {weightLines.length > 0 && (
            <div className="nia-hint" style={{ marginTop: 8 }}>
              {weightLines.map((l) => l.sku).join(', ')} {weightLines.length === 1 ? 'is a weight item' : 'are weight items'}:
              the quantities above are OUNCES, the unit these are stocked and costed in. Oils are ordered in pounds,
              but the pounds→ounces conversion already happened when the PO was written — converting again here
              would understate stock 16x.
            </div>
          )}
          {overReceipt.length > 0 && (
            <div className="nia-hint nia-tag-warn" style={{ marginTop: 8 }}>
              Receiving more than the open quantity on line{overReceipt.length === 1 ? '' : 's'}{' '}
              {overReceipt.map((l) => l.lineNumber).join(', ')} — the PO's receipt tolerance decides whether this posts.
            </div>
          )}

          <div className="nia-footer">
            <div className="nia-footer-totals">
              <span>Receiving <strong>{receiving.length}</strong> line{receiving.length === 1 ? '' : 's'}</span>
              <span className="nia-total-line">Value <strong>{money(totalValue)}</strong></span>
            </div>
            <div className="nia-actions">
              <button className="nia-btn nia-btn-primary" onClick={post} disabled={!canPost || writesEnabled === false}>
                {submitting ? 'Posting…' : amend.isAmend ? 'Reverse and re-receive' : 'Post receipt'}
              </button>
            </div>
          </div>
          <p className="nia-hint" style={{ marginTop: 8 }}>
            Posting creates a FIFO cost layer per line at PO cost, adds native on-hand at the chosen location,
            posts a DR inventory / CR 2035 received-not-billed journal entry, and advances the PO's received
            quantities. NetSuite gets no item receipt — key one in deliberately once it is reachable again.
          </p>
        </div>
      )}
    </div>
  );
}
